import { motion } from "framer-motion";
import { useState } from "react";
import {
  Check, ShieldCheck, Award, TrendingUp, Users, Sparkles, Target,
  CreditCard, Zap, MessageCircle, Calendar, Wand2, LineChart, Crown,
} from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import CheckoutDialog from "../components/CheckoutDialog";

const WHATSAPP_URL = "https://wa.me/5511989666465?text=Ol%C3%A1%2C+quero+contratar+o+Social+Media+";

/* ------------------------------ HERO ------------------------------ */
function Hero() {
  return (
    <section
      data-testid="sm-hero"
      className="relative pt-40 pb-24 md:pt-56 md:pb-32 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(10,77,255,0.35), transparent 60%), linear-gradient(180deg, #050505 0%, #030817 55%, #061436 100%)",
      }}
    >
      {/* Glow orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,_rgba(10,77,255,0.35),_transparent_60%)] blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(74,124,255,0.22),_transparent_60%)] blur-3xl" />
      </div>

      {/* Grid */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #6B8AFF 1px, transparent 1px), linear-gradient(to bottom, #6B8AFF 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue/30 bg-blue/[0.06] backdrop-blur-md text-[10px] uppercase tracking-[0.3em] text-blue-soft mb-8"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
          Serviço Exclusivo — Instagram
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          data-testid="sm-title"
          className="font-serif font-bold text-white tracking-tight leading-[1.02] text-5xl md:text-7xl lg:text-[6.5vw] max-w-5xl mx-auto"
        >
          Social Media{" "}
          <span className="bg-gradient-to-r from-blue-300 via-blue-soft to-white bg-clip-text text-transparent">
            Profissional
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.55 }}
          className="mt-8 text-white/65 text-lg md:text-xl leading-relaxed font-light max-w-3xl mx-auto"
        >
          Transformamos o Instagram da sua empresa em uma vitrine que gera{" "}
          <span className="text-white">autoridade, confiança e vendas</span>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.75 }}
          className="mt-12 flex flex-col sm:flex-row gap-4 items-center justify-center"
        >
          <a
            data-testid="sm-hero-cta"
            href="#planos"
            className="inline-flex items-center gap-3 rounded-full bg-blue-400 text-ink-950 px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold shadow-[0_0_40px_-4px_rgba(107,138,255,0.6)] hover:bg-blue-300 hover:shadow-[0_0_60px_-2px_rgba(107,138,255,0.9)] transition-all duration-500"
          >
            Ver planos
            <span>→</span>
          </a>
          <a
            href="#como-funciona"
            className="text-white/70 hover:text-blue text-xs uppercase tracking-[0.25em] transition-colors px-4 py-4"
          >
            Como funciona?
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------ BENEFITS ------------------------------ */
const BENEFITS = [
  { icon: ShieldCheck, title: "Aumente sua credibilidade", desc: "Um perfil bem construído transmite segurança e profissionalismo desde o primeiro contato." },
  { icon: Award, title: "Tenha um perfil profissional", desc: "Feed harmonioso, bio otimizada e destaque bem estruturado para impressionar." },
  { icon: TrendingUp, title: "Gere mais autoridade", desc: "Conteúdos que posicionam sua marca como referência no seu segmento." },
  { icon: Users, title: "Alcance mais clientes", desc: "Estratégia de conteúdo desenhada para atrair o público certo, todos os dias." },
  { icon: Target, title: "Conteúdo estratégico", desc: "Cada post tem propósito: educar, engajar, converter." },
  { icon: Sparkles, title: "Fortaleça sua marca", desc: "Identidade visual coerente que faz sua marca ser lembrada e reconhecida." },
];

function Benefits() {
  return (
    <section
      data-testid="sm-benefits"
      className="relative py-28 md:py-40 bg-ink-950 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(107,138,255,0.15),_transparent_65%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="text-[10px] uppercase tracking-[0.35em] text-blue-soft mb-4">Benefícios</div>
          <h2 className="font-serif font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Por que investir em{" "}
            <span className="bg-gradient-to-r from-blue-300 to-blue-soft bg-clip-text text-transparent">
              Social Media?
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                whileHover={{ y: -6 }}
                data-testid={`benefit-${i}`}
                className="group relative rounded-3xl p-8 border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl hover:border-blue-400/40 hover:bg-white/[0.05] transition-all duration-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
              >
                <div className="w-12 h-12 rounded-2xl bg-blue-400/10 ring-1 ring-blue-400/30 flex items-center justify-center mb-6 group-hover:bg-blue-400/20 group-hover:ring-blue-400/60 transition-all duration-500">
                  <Icon className="w-5 h-5 text-blue-soft" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-white mb-3 tracking-tight">
                  {b.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ PLANS ------------------------------ */
const PLANS = [
  {
    id: "start",
    icon: TrendingUp,
    name: "Start",
    price: "R$ 350",
    period: "/mês",
    tagline: "Ideal para empresas que desejam iniciar sua presença digital.",
    features: [
      "8 artes mensais",
      "Stories estratégicos",
      "Organização do perfil",
      "Legendas profissionais",
    ],
    cta: "Contratar Plano",
  },
  {
    id: "pro",
    icon: Sparkles,
    name: "Pro",
    price: "R$ 800",
    period: "/mês",
    tagline: "Ideal para empresas que desejam crescer de forma consistente.",
    features: [
      "12 artes mensais",
      "Planejamento estratégico",
      "Stories estratégicos",
      "4 Reels profissionais",
      "Conteúdo profissional",
      "Calendário mensal",
      "Legendas estratégicas",
    ],
    cta: "Quero Crescer",
    featured: true,
    badge: "★ MAIS CONTRATADO",
  },
  {
    id: "premium",
    icon: Crown,
    name: "Premium",
    price: "R$ 2.000",
    period: "/mês",
    tagline: "Ideal para empresas que desejam posicionamento profissional e crescimento constante.",
    features: [
      "16 artes mensais",
      "Stories frequentes",
      "8 Reels estratégicos",
      "Planejamento completo",
      "Calendário de conteúdo",
      "Legendas persuasivas",
      "Organização completa do perfil",
      "Acompanhamento de resultados",
      "Estratégia personalizada",
    ],
    cta: "Solicitar Orçamento",
    badge: "♛ PREMIUM",
  },
];

function PlanCard({ p, i, onContratar }) {
  const Icon = p.icon;
  const isPro = p.featured;
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      data-testid={`sm-plan-${p.id}`}
      className={`relative flex flex-col rounded-3xl backdrop-blur-2xl transition-all duration-500 group
        ${isPro
          ? "bg-gradient-to-b from-blue-500/[0.14] via-blue-500/[0.06] to-white/[0.02] border-2 border-blue-400/60 shadow-[0_0_80px_-10px_rgba(74,124,255,0.55),inset_0_1px_0_rgba(255,255,255,0.08)] lg:scale-[1.05] lg:-mt-4 z-10 p-10 md:p-12"
          : "bg-white/[0.03] border border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-blue-400/40 hover:bg-white/[0.05] p-8 md:p-10"}
      `}
    >
      {p.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold whitespace-nowrap
          ${isPro
            ? "bg-blue-400 text-ink-950 shadow-[0_0_30px_rgba(107,138,255,0.6)]"
            : "bg-gradient-to-r from-blue-300 to-blue-500 text-ink-950 shadow-[0_0_25px_rgba(107,138,255,0.4)]"}`}>
          {p.badge}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center backdrop-blur-md ${
          isPro ? "bg-blue-400/20 ring-1 ring-blue-400/50" : "bg-white/[0.05] ring-1 ring-white/10"
        }`}>
          <Icon className={`w-5 h-5 ${isPro ? "text-blue-300" : "text-blue-soft"}`} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-white tracking-tight font-semibold">
          Plano {p.name}
        </h3>
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-serif font-bold tracking-tight text-white text-5xl md:text-6xl">
          {p.price}
        </span>
        <span className="text-white/50 text-sm md:text-base">{p.period}</span>
      </div>

      <p className="mt-6 text-white/60 text-sm leading-relaxed pb-8 border-b border-white/10">
        {p.tagline}
      </p>

      <ul className="mt-8 space-y-3.5 flex-1">
        {p.features.map((f, k) => (
          <li key={k} className="flex items-start gap-3 text-white/80 text-sm leading-relaxed">
            <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
              isPro ? "bg-blue-400/20" : "bg-white/[0.06]"
            }`}>
              <Check className={`w-3 h-3 ${isPro ? "text-blue-300" : "text-blue-soft"}`} strokeWidth={2.5} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        data-testid={`sm-plan-cta-${p.id}`}
        onClick={() => onContratar(p)}
        className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-500
          ${isPro
            ? "bg-blue-400 text-ink-950 hover:bg-blue-300 shadow-[0_0_40px_-5px_rgba(107,138,255,0.7)] hover:shadow-[0_0_60px_-2px_rgba(107,138,255,0.9)]"
            : "bg-white/5 text-white border border-white/15 hover:bg-blue-400 hover:text-ink-950 hover:border-blue-400 hover:shadow-[0_0_40px_-5px_rgba(107,138,255,0.6)]"}
        `}
      >
        Contratar Agora
        <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
      </button>
    </motion.article>
  );
}

function Plans() {
  const [selected, setSelected] = useState(null);
  return (
    <section
      id="planos"
      data-testid="sm-plans"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 90% 60% at 50% 0%, rgba(10,77,255,0.28), transparent 60%), linear-gradient(180deg, #050505 0%, #030817 40%, #061436 75%, #0A2668 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 -left-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(10,77,255,0.35),_transparent_60%)] blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(74,124,255,0.25),_transparent_60%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-14"
        >
          <div className="flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.35em] text-blue-soft mb-6">
            <span className="w-8 h-px bg-blue-soft/60" />
            Planos Social Media
            <span className="w-8 h-px bg-blue-soft/60" />
          </div>
          <h2 className="font-serif font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Escolha o plano ideal para{" "}
            <span className="bg-gradient-to-r from-blue-300 via-blue-soft to-white bg-clip-text text-transparent">
              o seu Instagram
            </span>
            .
          </h2>
        </motion.div>

        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} p={p} i={i} onContratar={setSelected} />
          ))}
        </div>
      </div>

      <CheckoutDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        planId={selected ? `sm-${selected.id}` : undefined}
        planName={selected ? `Social Media ${selected.name}` : ""}
        priceLabel={selected ? `${selected.price}${selected.period || ""}` : ""}
      />
    </section>
  );
}

/* ------------------------------ PAYMENTS ------------------------------ */
const PAYMENTS = [
  { icon: CreditCard, title: "Cartão de Crédito", desc: "Todas as bandeiras aceitas" },
  { icon: CreditCard, title: "Cartão de Débito", desc: "Pagamento à vista" },
  { icon: Zap, title: "PIX", desc: "Aprovação instantânea" },
];

function Payments() {
  return (
    <section data-testid="sm-payments" className="relative py-24 md:py-36 bg-ink-950">
      <div className="relative z-10 mx-auto max-w-[1200px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] uppercase tracking-[0.35em] text-blue-soft mb-4">Pagamento</div>
          <h2 className="font-serif font-bold text-white text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
            Formas de Pagamento
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PAYMENTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                data-testid={`payment-${i}`}
                className="rounded-3xl p-8 border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl hover:border-blue-400/40 hover:bg-white/[0.05] transition-all duration-500 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-400/10 ring-1 ring-blue-400/30 flex items-center justify-center mx-auto mb-5">
                  <Icon className="w-6 h-6 text-blue-soft" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl text-white font-semibold mb-1">{p.title}</h3>
                <p className="text-white/50 text-sm">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mt-10 text-center text-white/50 text-sm"
        >
          Parcelamento disponível no cartão.
        </motion.p>
      </div>
    </section>
  );
}

/* ------------------------------ HOW IT WORKS ------------------------------ */
const STEPS = [
  { icon: MessageCircle, title: "Reunião de alinhamento", desc: "Conhecemos sua marca, público e objetivos." },
  { icon: Wand2, title: "Planejamento estratégico", desc: "Criamos calendário editorial e linha criativa." },
  { icon: Calendar, title: "Produção dos conteúdos", desc: "Artes, reels e legendas produzidos com padrão premium." },
  { icon: LineChart, title: "Publicação e acompanhamento", desc: "Postamos, monitoramos métricas e otimizamos." },
];

function HowItWorks() {
  return (
    <section
      id="como-funciona"
      data-testid="sm-how"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #050505 0%, #030817 55%, #061436 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(10,77,255,0.15),_transparent_65%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="text-[10px] uppercase tracking-[0.35em] text-blue-soft mb-4">Processo</div>
          <h2 className="font-serif font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Como funciona?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                data-testid={`step-${i}`}
                className="relative rounded-3xl p-8 border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl hover:border-blue-400/40 hover:bg-white/[0.05] transition-all duration-500 group"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-400/10 ring-1 ring-blue-400/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-soft" strokeWidth={1.5} />
                  </div>
                  <span className="font-serif text-4xl font-bold text-white/10 group-hover:text-blue/40 transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-lg md:text-xl text-white font-semibold mb-3 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ FINAL CTA ------------------------------ */
function FinalCTA() {
  return (
    <section
      data-testid="sm-final-cta"
      className="relative py-32 md:py-48 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 90% 70% at 50% 50%, rgba(10,77,255,0.35), transparent 70%), linear-gradient(180deg, #061436 0%, #0A2668 60%, #0A4DFF 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 -left-40 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(107,138,255,0.28),_transparent_60%)] blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(107,138,255,0.28),_transparent_60%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1200px] px-6 md:px-12 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1 }}
          data-testid="sm-final-headline"
          className="font-serif font-bold text-white tracking-tight leading-[1.02] text-4xl md:text-6xl lg:text-7xl max-w-4xl mx-auto"
        >
          Sua empresa merece um{" "}
          <span className="bg-gradient-to-r from-blue-200 via-white to-blue-soft bg-clip-text text-transparent">
            Instagram profissional
          </span>
          .
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-8 text-white/70 text-lg md:text-xl leading-relaxed font-light max-w-3xl mx-auto"
        >
          Pare de postar sem estratégia. Nossa equipe cria conteúdos que fortalecem sua marca, aumentam sua autoridade e aproximam você dos seus clientes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.35 }}
          className="mt-14"
        >
          <a
            data-testid="sm-final-cta-btn"
            href={`${WHATSAPP_URL}Social+Media`}
            target="_blank"
            rel="noreferrer"
            className="group relative inline-flex items-center gap-4 rounded-full bg-white text-ink-950 pl-10 pr-4 py-5 font-semibold text-sm uppercase tracking-[0.28em] shadow-[0_0_60px_-2px_rgba(255,255,255,0.4),0_0_120px_-10px_rgba(107,138,255,0.8)] hover:shadow-[0_0_100px_-4px_rgba(255,255,255,0.6),0_0_180px_-10px_rgba(107,138,255,1)] transition-all duration-500 hover:scale-[1.03]"
          >
            Quero meu Social Media
            <span className="w-12 h-12 rounded-full bg-ink-950 text-white flex items-center justify-center transition-transform duration-500 group-hover:rotate-45">
              →
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------ PAGE ------------------------------ */
export default function SocialMedia() {
  return (
    <main data-testid="social-media-page" className="bg-ink-950 text-white/90">
      <Nav />
      <Hero />
      <Benefits />
      <Plans />
      <Payments />
      <HowItWorks />
      <FinalCTA />
      <Footer />
    </main>
  );
}
